import React, { FC, useCallback, useEffect, useState } from 'react';
import { breakpoints } from '../../styles/theme';
import { getData } from './helpers';
import useTranslations from '@hooks/useTranslations';
import { getMPGfromLitterPerKM } from '@utils/Characteristics.utils';
import { FUEL_TYPES, isBrandOV, MARKET } from '../../constants/main';
import { useMedia } from 'react-use';
import { useMoneyFormatter } from '@hooks/useMoneyFormatter';
import { ICarDetail } from '../../redux/carDetails/carDetails.duck.interface';
import { useFeatureSwitchEnabled } from '@hooks/useFeatureSwitchEnabled';
import { FEATURES_LIST } from '../../context/featureSwitch';
import { publicAssets } from '@utils/url.utils';
import { useCarDetailsDuck } from '@hooks/useCarDetailsDuck';
import { getBootSpaceValue } from '@components/Characteristics/helper/getStockEnviromentalData';

const TechPointsTemplate: FC<{
    setCarNameOuterVisibility?: (value: boolean) => void;
    showBootSpace?: boolean;
    showPower?: boolean;
}> = ({ setCarNameOuterVisibility, showBootSpace = false, showPower = true }) => {
    const { t } = useTranslations();
    const { carDetails } = useCarDetailsDuck();
    const isSmallBreakPointOrWider = useMedia(`(min-width: ${breakpoints.sm}px)`, true);
    const [carInfoVisibility, setCarInfoVisibility] = useState<boolean>(false);
    const fuelLower = carDetails.fuel?.toLowerCase();
    const isBenzin = FUEL_TYPES.BENZIN.split(';').includes(fuelLower);
    const isDiesel = FUEL_TYPES.DIESEL.split(';').includes(fuelLower);
    const isHybrid = FUEL_TYPES.HYBRID.split(';').includes(fuelLower);
    const isBenzinOrDiesel = isDiesel || isBenzin;

    const getPower = (carDetails: ICarDetail) => {
        return getData(carDetails.engine.specs, 'power', 'value');
    };

    const getTaxBandKey = (carDetails: ICarDetail) => {
        return getData(carDetails.engine.specs, 'taxBand', 'value');
    };

    useEffect(() => {
        if (isSmallBreakPointOrWider && carInfoVisibility === false) {
            setCarInfoVisibility(true);
        }
    }, [isSmallBreakPointOrWider]);

    useEffect(() => {
        if (isSmallBreakPointOrWider && setCarNameOuterVisibility) {
            setCarNameOuterVisibility(true);
        }

        if (!isSmallBreakPointOrWider && setCarNameOuterVisibility) {
            setCarNameOuterVisibility(!carInfoVisibility);
        }
    }, [isSmallBreakPointOrWider, carInfoVisibility]);

    const consumptionStars: string = isBrandOV ? '**' : '***';

    const isVisibleCO2Value = useFeatureSwitchEnabled(FEATURES_LIST.FEATURE_SWITCH_SHOW_CO2_VALUES);
    const localConsumption = getData(carDetails.engine.specs, 'consumptionCombined', 'local_value');
    const consumption = localConsumption ?? getData(carDetails.engine.specs, 'consumptionCombined', 'value');
    const localElectricRange = getData(carDetails.engine.specs, 'electricRange', 'local_value');
    const electricRange = localElectricRange ?? getData(carDetails.engine.specs, 'electricRange', 'value');

    return (
        <div>
            {showBootSpace && getBootSpaceValue(carDetails) && (
                <div className="tech-point">
                    <span>{t('configurator.info.boot')}</span>
                    <strong>{getBootSpaceValue(carDetails)}</strong>
                </div>
            )}
            {isBenzinOrDiesel && (
                <>
                    {showPower && (
                        <div className="tech-point">
                            <span data-testid="TESTING_TECH_INFO_POWER">{t('configurator.info.power')}</span>
                            <strong>{getPower(carDetails)}</strong>
                        </div>
                    )}
                    <div className="tech-point">
                        <span>{t('configurator.info.mileage')}</span>
                        <div data-testid="TESTING_TECH_INFO_CONSUMPTION">
                            <strong>{`${consumption} ${t('configurator.info.consumptionCombined.label')}`}</strong>
                        </div>
                    </div>
                    <div className="tech-point emmisions">
                        <span>
                            {t('configurator.info.emmisions')}
                            <sub>2</sub>
                        </span>
                        <div className="emmisions-value" data-testid="TESTING_TECH_INFO_EMMISION">
                            <strong>{`${getData(carDetails.engine.specs, 'co2Combined', 'value')} ${t(
                                'configurator.info.emmisions.label'
                            )}`}</strong>
                        </div>
                    </div>
                </>
            )}
            {isHybrid && (
                <>
                    {showPower && (
                        <div className="tech-point">
                            <span>{t('technicalFeatures.environmentalDetails.consumption.electricRange')}</span>
                            <strong>
                                {electricRange} {t('configurator.info.power.label')}
                            </strong>
                        </div>
                    )}
                    <div className="tech-point">
                        <span>{t('technicalFeatures.environmentalDetails.consumption.electricConsumption')}</span>
                        <strong>{getData(carDetails.engine.specs, 'electricConsumption', 'value')}</strong>
                    </div>
                    <div className="tech-point">
                        <span>{t('configurator.info.mileage')}</span>
                        <strong>{`${getData(
                            carDetails.engine.specs,
                            'consumptionCombined',
                            'value'
                        )} l/100km${consumptionStars}`}</strong>
                    </div>
                    <div className="tech-point emmisions">
                        <span>
                            {t('configurator.info.emmisions')}
                            <sub>2</sub>
                        </span>
                        <div className="emmisions-value">
                            <strong>{`${getData(
                                carDetails.engine.specs,
                                'co2Combined',
                                'value'
                            )} g/km${consumptionStars}`}</strong>
                        </div>
                    </div>
                </>
            )}
            {!isHybrid && !isBenzinOrDiesel && (
                <>
                    {showPower && (
                        <div className="tech-point">
                            <span>{t('technicalFeatures.environmentalDetails.consumption.electricRange')}</span>
                            <strong>
                                {electricRange} {t('configurator.info.power.label')}
                            </strong>
                        </div>
                    )}
                    <div className="tech-point">
                        <span>{t('technicalFeatures.environmentalDetails.consumption.electricConsumption')}</span>
                        <strong>
                            {getData(carDetails.engine.specs, 'electricConsumption', 'value')}{' '}
                            {t('technicalFeatures.environmentalDetails.consumption.electricConsumption.label')}
                        </strong>
                    </div>
                    <div className="tech-point emmisions">
                        <span>
                            {t('configurator.info.emmisions')}
                            <sub>2</sub>
                        </span>
                        <div className="emmisions-value">
                            <strong>{`${getData(
                                carDetails.engine.specs,
                                'co2Combined',
                                'value'
                            )} g/km${consumptionStars}`}</strong>
                        </div>
                    </div>
                </>
            )}
            {isVisibleCO2Value && getTaxBandKey(carDetails) && (
                <div className="tech-point co2">
                    <span>{t('configurator.info.co2')}</span>
                    <div className="co2" data-testid="TESTING_TECH_INFO_CO2">
                        <img
                            src={publicAssets(
                                `/public/images/co2/${MARKET}/farbskala_gross_${getTaxBandKey(carDetails)}.jpg`
                            )}
                        />
                    </div>
                </div>
            )}
        </div>
    );
};

export default TechPointsTemplate;
